import mixpanel from 'mixpanel-browser';
import type { RouteLocationNormalized } from 'vue-router';
import constants from '@/config/constants';
import router from '@/router';
import useAuthStore from '@/stores/auth';
// @TODO REMOVE
// @ts-ignore
import { useInternationalization } from '@/i18n';
import { getBrowserProperties } from '@/utils/browserInfo';
import { useAnalyticsApi } from '@/composables/api/analytics/useAnalyticsApi';
import type { EventProperties, RouteData } from '@/types/analytics';
import { isProduction } from '@/utils/environment';
import type { TourRecommendationCode } from '@/composables/api/usageAnalysis/types';

import '@/types/analytics';

const hasUserConsented = (): boolean => {
  return Boolean(window.cookiehub?.hasConsented('analytics'));
};

const isUserEmailAllowed = (
  authStore: ReturnType<typeof useAuthStore>
): boolean => {
  // @ts-ignore
  const userProfile = authStore.userProfile?.profile;
  if (!userProfile?.email) return true;
  const userEmailDomain = userProfile.email?.split('@')[1];
  return (
    !userEmailDomain ||
    !constants.internalEmails.includes(userEmailDomain) ||
    !isProduction
  );
};

const getRouteData = (
  route: RouteLocationNormalized | undefined
): RouteData => {
  if (!route) return { routeName: '', context: '' };
  const { name, params, matched } = route;

  return {
    routeName: (name as string) || '',
    ...params,
    context: (matched?.[0]?.name as string) || ''
  };
};

const trackEvent = (eventName: string, properties?: EventProperties): void => {
  const authStore = useAuthStore();

  // Check if user can be tracked
  if (!hasUserConsented() || !isUserEmailAllowed(authStore)) return;

  const { currentLanguage } = useInternationalization();
  const { trackAnalyticsEvent } = useAnalyticsApi();

  // @ts-ignore
  const userId = authStore.userProfile?.profile?.id || null;

  const eventData: EventProperties = {
    ...properties,
    ...getRouteData(router?.currentRoute?.value),
    roles: authStore.userRoles,
    selectedLanguage: currentLanguage.value || '',
    appVersion: import.meta.env.VITE_VERSION,
    // Browser/device properties (Mixpanel equivalents)
    ...getBrowserProperties(userId)
  };

  trackAnalyticsEvent(eventName, eventData);
};

// Identify user in Mixpanel for session recording association only
const identifyUser = (userId: string): void => {
  mixpanel.identify(userId);
};

// Event tracking functions
const trackingEvents = {
  viewPage: (page: string, from: string, other?: EventProperties): void => {
    trackEvent(`View ${page}`, { from, ...other });
  },
  user: (userId: string, tenants: string[], language?: string): void =>
    trackEvent('User', { userId, tenants, language }),
  identify: identifyUser,
  login: (): void => trackEvent('Login'),
  logout: (): void => trackEvent('Logout'),
  clickQSTab: (tab: string, dashboard: string): void =>
    trackEvent('Click QS Tab', { tab, dashboard }),
  changeAddressCorrectionTab: (tab: string): void =>
    trackEvent('Change Address Correction Tab', { tab }),
  rejectAddressCorrection: (): void => trackEvent('Reject Address Correction'),
  approveAddressCorrection: (): void =>
    trackEvent('Approve Address Correction'),
  changeSuco: (): void => trackEvent('Change Suco'),
  multiActivateDrivers: (type: string): void =>
    trackEvent('Multi Activate Drivers', { type }),
  multiDeleteDrivers: (type: string): void =>
    trackEvent('Multi Delete Drivers', { type }),
  openLSMapMarker: (tourPercentage: number, tourStatus: string): void =>
    trackEvent('Open Live Status Map Marker', {
      tourPercentage,
      tourStatus
    }),
  addSubcontractor: (): void => trackEvent('Add Subcontractor'),
  deleteSubcontractor: (): void => trackEvent('Delete Subcontractor'),
  updateSubcontractor: (): void => trackEvent('Update Subcontractor'),
  openTDV: (source: string): void => trackEvent('Open TDV', { source }),
  downloadTableData: (tableId: string, format: string): void =>
    trackEvent(`Download Table ${tableId}`, { format }),
  activateDriver: (): void => trackEvent('Activate Driver'),
  deactivateDriver: (): void => trackEvent('Deactivate Driver'),
  deleteDriver: (): void => trackEvent('Delete Driver'),
  editDriver: (): void => trackEvent('Edit Driver'),
  openTourHistory: (source: string): void =>
    trackEvent('Open Tour History', { source }),
  rotateLSScreen: (orientation: string): void =>
    trackEvent('Rotate Live Status Screen', { orientation }),
  clickAddressErrors: (): void => trackEvent('Click Address Errors'),
  clickConflictsTDV: (): void => trackEvent('Click Conflicts'),
  clickHealthStatusDepot: (health: string): void =>
    trackEvent('Click Health Status Depot', { health }),
  editUser: (): void => trackEvent('Edit User'),
  deleteUser: (): void => trackEvent('Delete User'),
  addUser: (): void => trackEvent('Add User'),
  editPermissions: (): void => trackEvent('Edit Permission'),
  openHelpCenter: (): void => trackEvent('Open Help Center'),
  clickPerformanceDetails: (): void => trackEvent('Click Performance Details'),
  openSelectInput: (identifier: string): void =>
    trackEvent('Open Select Input', { identifier }),
  viewDeviceInfo: (page: string): void =>
    trackEvent('View Device Info', { page }),
  viewTimeFrameDetails: (): void => trackEvent('View Time Frame Details'),
  clickDrivenHistoryControls: (action: string): void =>
    trackEvent('Click Driven History Controls', { action }),
  openDrivenHistorySequence: (source: string): void =>
    trackEvent('Open Driven History Sequence', { source }),
  clickJumpToMapLiveStatus: (): void =>
    trackEvent('Click Jump To Map Live Status'),
  clickJumpToTableLiveStatus: (): void =>
    trackEvent('Click Jump To Table Live Status'),
  openTDHTourInfo: (): void => trackEvent('Open Tour Driven History Info'),
  openTDHTourLegend: (): void => trackEvent('Open Tour Driven History Legend'),
  sortingChanged: (column: unknown): void =>
    trackEvent('Sorting Changed', { column }),
  clickCheckRecommendationButton: (nrOfTours: EventProperties): void =>
    trackEvent('Check Recommendation Button Clicked', nrOfTours),
  hoverTourRecommendationIcon: (recommendation: TourRecommendationCode): void =>
    trackEvent('Hover Tour Recommendation Icon', { recommendation }),
  goToBetterPlanner: (source: string): void =>
    trackEvent('Go to Better Planner', { source }),
  clickWidget: (widgetName: string): void =>
    trackEvent(`Click "${widgetName}" Widget`),
  clickTodoWidgetItem: (todoItem: string): void =>
    trackEvent(`Click "${todoItem}" in Todos Widget`),
  clickCompareTDHTab: (): void => trackEvent('Click Compare TDH Tab'),
  clickInvestigateTDHTab: (): void => trackEvent('Click Investigate TDH Tab'),
  clickWPListItem: (source: string): void =>
    trackEvent(`Click Waypoint List Item`, { source }),
  clickTDHMarker: (source: string): void =>
    trackEvent('Click TDH Marker', { source }),
  clickOpenWPJobDetails: (source: string): void =>
    trackEvent('Click Open WP Job Details', { source }),
  depotInsightsFeedback: (positive: boolean): void =>
    trackEvent('Depot Insights Feedback', { positive }),
  depotInsightsComment: (comment: string): void =>
    trackEvent('Depot Insights Comment', {
      comment: comment || 'empty'
    }),
  depotInsightsWidgetClicked: (): void =>
    trackEvent('Depot Insights Widget Clicked'),
  keyStatisticsInsightsViewMoreClicked: (): void =>
    trackEvent('Key Statistics View More Clicked'),
  keyStatisticsInsightsFeedback: (positive: boolean): void =>
    trackEvent('Key Statistics Insights Feedback', { positive }),
  keyStatisticsInsightsComment: (comment: string): void =>
    trackEvent('Key Statistics Insights Comment', {
      comment: comment || 'empty'
    })
};

export default trackingEvents;
